import type { ColorScheme } from '../../../amplienceTypes/schemas/exported/usp-list-schema';
import type { ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { AmplienceCmsLink } from '../core/link/AmplienceCmsLink';
import { animations, colors, media } from '../core/styles';

const S = {
  USPBarItem: styled.li`
    display: inline-block;
    min-width: 220px;
    box-sizing: border-box;
    height: var(--usp-bar-height);
    font-variant: tabular-nums;

    &:nth-child(n + 4) {
      display: inline-block;

      @media ${({ theme }) => media(theme).greaterThan('sm')} {
        display: none;
      }
    }

    &.scrollable {
      display: none;
    }

    &.active {
      display: inline-block;
      animation: ${animations.headerUspsScroll} 5200ms ease-in-out infinite;

      @media (prefers-reduced-motion) {
        animation: none;
      }
    }

    @media ${({ theme }) => media(theme).lessThan('sm')} {
      &.ppe div {
        padding: 0 8px;
      }
    }
  `,

  ItemWrapper: styled.div`
    padding: 0 28px;
    font-weight: 400;
    font-size: 12px;
    line-height: var(--usp-bar-height);
  `,

  ItemLink: styled(AmplienceCmsLink)<{
    $colorScheme?: ColorScheme;
  }>`
    text-decoration: none;
    color: ${({ $colorScheme, theme }) =>
      $colorScheme === 'PPE' ? colors.WHITE : theme.colors.content.primary};

    &:hover,
    &:focus {
      color: ${colors.ACCESSIBILITY_GREY};
    }
  `,

  Item: styled.div<{
    $colorScheme?: ColorScheme;
  }>`
    color: ${({ $colorScheme, theme }) =>
      $colorScheme === 'PPE' ? colors.WHITE : theme.colors.content.primary};
  `,
};

export interface USPItemContainerProps {
  url?: string;
  target?: string;
  children: ReactNode;
  colorScheme?: ColorScheme;
  className?: string;
  suppressHydrationWarning?: boolean;
}

export const USPItemContainer = ({
  url,
  target,
  children,
  colorScheme,
  ...rest
}: USPItemContainerProps): ReactElement => (
  <S.USPBarItem {...rest}>
    <S.ItemWrapper>
      {url ? (
        <S.ItemLink href={url ?? '#'} target={target} $colorScheme={colorScheme}>
          {children}
        </S.ItemLink>
      ) : (
        <S.Item>{children}</S.Item>
      )}
    </S.ItemWrapper>
  </S.USPBarItem>
);
